import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Divider, IconButton, List, ListItem,
  ListItemText, Stack, Tooltip, Typography,
} from '@mui/material';
import {
  EditNote as DraftIcon, Mic as MicIcon, ArrowForward as GoIcon,
  DeleteOutline as DiscardIcon, HistoryToggleOff as GoneIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** Hours, as something a person would say. */
function expiry(left) {
  if (left === null || left === undefined) return null;
  if (left <= 0) return 'expiring now';
  if (left < 1) return `${Math.round(left * 60)} min left`;
  if (left < 24) return `${Math.round(left)}h left`;
  // Whole days still remaining, rounded down — the same count the sidebar gives, which
  // used to say "2 days" beside this list's "3 days" for the same visit.
  const d = Math.floor(left / 24);
  return `${d} day${d === 1 ? '' : 's'} left`;
}

function when(iso) {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
  });
}

/**
 * Every visit this clinician started and has not finished — the ones still holding a
 * recording, and the ones whose recording has since expired.
 *
 * The dashboard card only shows the first kind, because its job is to get somebody back
 * to a consultation before the audio goes. That deadline is the only thing that makes a
 * visit urgent. This page is the other half of the answer: when the recording expires
 * the visit does not disappear, it just stops being urgent, and it is listed here with
 * its note, transcript and vitals intact.
 */
export default function Drafts() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [retention, setRetention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/v1/clinical/sessions/drafts`);
      setSessions(data?.sessions || []);
      setRetention(data?.retention_label || null);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const discard = async () => {
    setBusy(true);
    try {
      const { data } = await axios.delete(
        `${API_URL}/api/v1/clinical/session/${confirm.session_id}`);
      toast.success(data?.message || 'Visit discarded');
      setSessions((prev) => prev.filter((x) => x.session_id !== confirm.session_id));
      setConfirm(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not discard that visit');
    } finally {
      setBusy(false);
    }
  };

  const open = (s) => {
    if (s.resume === 'review') navigate(`/review/${s.session_id}`);
    else navigate(`/scribe/${s.patient_id}`);
  };

  const withAudio = sessions.filter((s) => s.has_audio);
  // Written by hand never had a recording, so it is not "expired" — it has its own group.
  const handWritten = sessions.filter((s) => !s.has_audio && s.is_manual);
  const expired = sessions.filter((s) => !s.has_audio && !s.is_manual);

  const row = (s) => {
    const countdown = expiry(s.audio_expires_in_hours);
    return (
      <ListItem
        key={s.session_id}
        divider
        secondaryAction={
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Discard this visit">
              <IconButton size="small" onClick={() => setConfirm(s)}>
                <DiscardIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button size="small" variant="outlined" endIcon={<GoIcon />} onClick={() => open(s)}>
              {s.resume === 'review' ? 'Review' : 'Resume'}
            </Button>
          </Stack>
        }
        sx={{ px: 0 }}
      >
        <ListItemText
          primary={
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Typography fontWeight={600}>{s.patient_name}</Typography>
              {s.has_note && <Chip size="small" icon={<DraftIcon />} label="Note drafted" color="primary" />}
              {s.is_manual
                ? <Chip size="small" icon={<DraftIcon />} label="Written by hand" variant="outlined" />
                : s.has_audio
                ? <Chip size="small" icon={<MicIcon />} label={countdown ? `Recording · ${countdown}` : 'Recording'}
                    color={s.audio_expires_in_hours !== null && s.audio_expires_in_hours <= 14 ? 'error' : 'default'} />
                : <Chip size="small" icon={<GoneIcon />} label="Recording expired" variant="outlined" />}
              {!s.has_note && s.has_transcript && <Chip size="small" label="Transcript only" variant="outlined" />}
            </Stack>
          }
          secondary={`${s.reason || 'No reason recorded'} · started ${when(s.started_at)}`}
        />
      </ListItem>
    );
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={700}>Drafts</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 680 }}>
        Visits you started and have not finished. Recordings are kept for
        {' '}{retention || 'a limited period'} and then deleted, finalized or not — but the
        visit stays here either way, with its note, transcript and vitals exactly as you
        left them.
      </Typography>

      {sessions.length === 0 ? (
        <Card><CardContent sx={{ py: 5, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Nothing unfinished. Every visit you have started has been finalized or discarded.
          </Typography>
        </CardContent></Card>
      ) : (
        <>
          {withAudio.length > 0 && (
            <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'warning.main' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700}>
                  Still recorded ({withAudio.length})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  The consultation can still be played back. Finish these first — the audio
                  goes on a clock.
                </Typography>
                <Divider />
                <List dense disablePadding>{withAudio.map(row)}</List>
              </CardContent>
            </Card>
          )}

          {handWritten.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700}>
                  Written by hand ({handWritten.length})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Notes you are writing yourself, with no recording — so no deadline. Open
                  one to carry on where you left off.
                </Typography>
                <Divider />
                <List dense disablePadding>{handWritten.map(row)}</List>
              </CardContent>
            </Card>
          )}

          {expired.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700}>
                  Recording expired ({expired.length})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  The audio has been deleted, so there is no longer a deadline. Everything
                  written down is still here and these can be finished at any time.
                </Typography>
                <Divider />
                <List dense disablePadding>{expired.map(row)}</List>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <Dialog open={Boolean(confirm)} onClose={() => !busy && setConfirm(null)}>
        <DialogTitle>Discard this visit?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.patient_name}&rsquo;s unfinished visit will be thrown away, along with
            its recording and any drafted note. Vitals already recorded are kept, because
            somebody really did measure them. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Keep it</Button>
          <Button onClick={discard} color="error" variant="contained" disabled={busy}>
            {busy ? 'Discarding…' : 'Discard'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
